import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { LuCalendarDays } from "react-icons/lu";
import { GoPencil } from "react-icons/go";
import toast from "react-hot-toast";

import "./AddAppointmentModal.css";
import CustomTimePicker from "./CustomTimePicker";
import DurationPicker from "./DurationPicker";
import CustomSelect from "./CustomSelect";
import ConfirmModal from "./ConfirmModal";
import ClientDropdown from "./ClientDropdown";
import { REST_API_URL } from "../api/config";

const API_URL = REST_API_URL;

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (code) => CURRENCY_SYMBOL_MAP[code] || code || "$";

const ENGLISH_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const RUSSIAN_MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const normalizePriceInput = (value, currencySymbol) => {
  if (!value) return "";

  let raw = String(value);

  raw = raw.replace(currencySymbol, "");
  raw = raw.replace(/,/g, ".");
  raw = raw.replace(/[^\d.]/g, "");

  const firstDotIndex = raw.indexOf(".");
  if (firstDotIndex !== -1) {
    const integerPart = raw.slice(0, firstDotIndex).replace(/\./g, "");
    const decimalPart = raw
      .slice(firstDotIndex + 1)
      .replace(/\./g, "")
      .slice(0, 2);

    raw = `${integerPart}.${decimalPart}`;
  } else {
    raw = raw.replace(/\./g, "");
  }

  if (!raw) return "";
  return `${currencySymbol}${raw}`;
};

const parsePriceToNumber = (value, currencySymbol) => {
  if (!value) return 0;

  let raw = String(value);

  raw = raw.replace(currencySymbol, "");
  raw = raw.replace(/,/g, ".");
  raw = raw.replace(/[^\d.]/g, "");

  const firstDotIndex = raw.indexOf(".");
  if (firstDotIndex !== -1) {
    const integerPart = raw.slice(0, firstDotIndex).replace(/\./g, "");
    const decimalPart = raw
      .slice(firstDotIndex + 1)
      .replace(/\./g, "")
      .slice(0, 2);

    raw = `${integerPart}.${decimalPart}`;
  } else {
    raw = raw.replace(/\./g, "");
  }

  const parsed = parseFloat(raw);
  if (Number.isNaN(parsed)) return 0;

  return Number(parsed.toFixed(2));
};

const parseDateString = (dateStr) => {
  if (!dateStr) return new Date();

  const parts = String(dateStr).trim().split(" ");
  if (parts.length < 2) return new Date();

  const monthName = parts[0];
  const day = parseInt(parts[1], 10);

  let monthIndex = ENGLISH_MONTHS.findIndex(
    (m) => m.toLowerCase() === monthName.toLowerCase(),
  );

  if (monthIndex === -1) {
    monthIndex = RUSSIAN_MONTHS.findIndex((m) =>
      monthName.toLowerCase().includes(m.toLowerCase()),
    );
  }

  return new Date(
    new Date().getFullYear(),
    monthIndex === -1 ? new Date().getMonth() : monthIndex,
    Number.isNaN(day) ? new Date().getDate() : day,
  );
};

const getWorkerLabel = (workerLike) => {
  if (!workerLike) return "My appointments";
  return (
    workerLike.full_name ||
    workerLike.name ||
    workerLike.username ||
    workerLike.email ||
    "My appointments"
  );
};

const extractWorkerId = (userData) => {
  const user = userData?.user || {};
  const normalizedStatus = normalizeStatus(user?.status);

  if (normalizedStatus === "worker" && userData?.id) {
    return userData.id;
  }

  const directCandidates = [
    userData?.worker_id,
    userData?.worker,
    userData?.worker_profile_id,
    userData?.worker_profile?.id,
    user?.worker_id,
    user?.worker,
    user?.worker_profile_id,
    user?.worker_profile?.id,
  ].filter(Boolean);

  if (directCandidates.length > 0) {
    return directCandidates[0];
  }

  const workers = Array.isArray(userData?.workers) ? userData.workers : [];

  const userIdCandidates = [
    user?.id,
    user?.pk,
    user?.user_id,
    userData?.user_id,
  ].filter(Boolean);

  const matchedWorker = workers.find((w) => {
    if (!w) return false;

    if (
      userIdCandidates.some(
        (candidate) => String(w?.user) === String(candidate),
      )
    ) {
      return true;
    }

    if (
      userIdCandidates.some(
        (candidate) => String(w?.user_id) === String(candidate),
      )
    ) {
      return true;
    }

    if (
      userIdCandidates.some(
        (candidate) => String(w?.worker_id) === String(candidate),
      )
    ) {
      return true;
    }

    return false;
  });

  if (matchedWorker?.id) {
    return matchedWorker.id;
  }

  if (workers.length === 1 && workers[0]?.id) {
    return workers[0].id;
  }

  return null;
};

const resolveCurrentWorker = (userData) => {
  const user = userData?.user || {};
  const workers = Array.isArray(userData?.workers) ? userData.workers : [];

  const extractedId = extractWorkerId(userData);

  if (extractedId) {
    const matched = workers.find((w) => String(w?.id) === String(extractedId));

    if (matched) {
      return {
        id: matched.id,
        name: getWorkerLabel(matched),
      };
    }

    return {
      id: extractedId,
      name:
        getWorkerLabel(user) ||
        `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
        user?.email ||
        "My appointments",
    };
  }

  return {
    id: null,
    name:
      getWorkerLabel(user) ||
      `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
      user?.email ||
      "My appointments",
  };
};

function CalendarDropdown({
  id,
  openDropdown,
  setOpenDropdown,
  value,
  onChange,
}) {
  const today = new Date();

  const getEnglishMonth = (dateString) => {
    if (!dateString) return ENGLISH_MONTHS[today.getMonth()];

    const monthName = dateString.split(" ")[0];
    const foundMonth = ENGLISH_MONTHS.find(
      (m) => m.toLowerCase() === monthName.toLowerCase(),
    );

    if (foundMonth) return foundMonth;

    const russianIndex = RUSSIAN_MONTHS.findIndex((m) =>
      monthName.toLowerCase().includes(m.toLowerCase()),
    );

    return russianIndex !== -1
      ? ENGLISH_MONTHS[russianIndex]
      : ENGLISH_MONTHS[today.getMonth()];
  };

  const getDayFromDate = (dateString) => {
    if (!dateString) return today.getDate();
    const parts = dateString.split(" ");
    const day = parseInt(parts[1], 10);
    return Number.isNaN(day) ? today.getDate() : day;
  };

  const [selectedMonth, setSelectedMonth] = useState(getEnglishMonth(value));
  const [selectedDay, setSelectedDay] = useState(getDayFromDate(value));
  const isOpen = openDropdown === id;

  useEffect(() => {
    setSelectedMonth(getEnglishMonth(value));
    setSelectedDay(getDayFromDate(value));
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".calendar-dropdown")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setOpenDropdown]);

  const toggleOpen = () => setOpenDropdown(isOpen ? null : id);

  const handleSave = () => {
    setOpenDropdown(null);
    onChange(`${selectedMonth} ${selectedDay}`);
  };

  return (
    <div className="appointment-page calendar-dropdown">
      <div className="appointment-page calendar-input" onClick={toggleOpen}>
        <span className="appointment-page icon">
          <LuCalendarDays />
        </span>
        <input type="text" value={`${selectedMonth} ${selectedDay}`} readOnly />
        <span className="appointment-page edit-icon">
          <GoPencil />
        </span>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="appointment-page calendar-popup"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="appointment-page month-selector">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {ENGLISH_MONTHS.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="appointment-page calendar-days">
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <div
                  key={d}
                  className={`appointment-page day ${
                    d === selectedDay ? "active" : ""
                  }`}
                  onClick={() => setSelectedDay(d)}
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="appointment-page calendar-buttons">
              <button
                type="button"
                className="appointment-page cancel"
                onClick={() => setOpenDropdown(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="appointment-page save"
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AddAppointmentModal({ isOpen, onClose, onSuccess }) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [appointmentStarted, setAppointmentStarted] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialDataLoading, setInitialDataLoading] = useState(false);

  const startRef = useRef(null);
  const endRef = useRef(null);
  const currentWorkerIdRef = useRef(null);

  const today = new Date();
  const defaultDate = `${today.toLocaleString("en-US", {
    month: "long",
  })} ${today.getDate()}`;

  const [formData, setFormData] = useState({
    client: "",
    client_id: null,
    master: "",
    worker_id: null,
    service_type: "",
    service_id: null,
    service_name: "",
    is_other_service: false,
    price: "",
    date: defaultDate,
    time: "08:00",
    duration: { hours: 1, minutes: 0 },
    real_estate_id: null,
  });

  const [workers, setWorkers] = useState([]);
  const [services, setServices] = useState([]);
  const [realEstates, setRealEstates] = useState([]);

  const [userStatus, setUserStatus] = useState(null);
  const [userCurrency, setUserCurrency] = useState("USD");
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [currentWorkerId, setCurrentWorkerId] = useState(null);
  const [currentWorkerName, setCurrentWorkerName] = useState("");

  const normalizedUserStatus = useMemo(
    () => normalizeStatus(userStatus),
    [userStatus],
  );

  const isIndividual = normalizedUserStatus === "individual";
  const isWorker = normalizedUserStatus === "worker";

  const filteredWorkers = useMemo(() => {
    if (formData.service_type === "service" && formData.service_id) {
      const selectedService = services.find((s) => s.id === formData.service_id);
      if (Array.isArray(selectedService?.workers) && selectedService.workers.length > 0) {
        return workers.filter((w) => selectedService.workers.includes(w.id));
      }
    }
    return workers;
  }, [workers, services, formData.service_type, formData.service_id]);
  const shouldShowMasterSelect = !isIndividual && !isWorker;

  const effectiveWorkerId =
  formData.worker_id || currentWorkerId || currentWorkerIdRef.current || null;

  const triggerStartAppointment = () => {
    if (!appointmentStarted && typeof window.gtag === "function") {
      window.gtag("event", "start_appointment_in_calendar");
      setAppointmentStarted(true);
    }
  };

  useEffect(() => {
    currentWorkerIdRef.current = currentWorkerId || null;
  }, [currentWorkerId]);

  useEffect(() => {
    if (!isOpen) return;

    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) return;

    const fetchAllData = async () => {
      setInitialDataLoading(true);

      try {
        const headers = buildHeaders(token);

        const userRes = await fetch(`${API_URL}/auth/userinfo/`, { headers });
        if (!userRes.ok) {
          throw new Error("Failed to load user info");
        }

        const userData = await userRes.json();

        const nextStatus = userData?.user?.status || null;
        const normalizedNextStatus = normalizeStatus(nextStatus);

        setUserStatus(nextStatus);

        const currency = userData?.currency || "USD";
        setUserCurrency(currency);
        setCurrencySymbol(getCurrencySymbol(currency));

        if (Array.isArray(userData?.workers) && userData.workers.length > 0) {
          const formattedWorkers = userData.workers.map((w) => ({
            id: w.id,
            name: w.full_name || w.email || "Unnamed master",
            image: w.image_url || null,
            registered: Boolean(w.full_name && w.full_name.trim().length > 0),
          }));
          setWorkers(formattedWorkers);
        } else {
          setWorkers([]);
        }

        if (normalizedNextStatus === "worker") {
          const forcedWorkerId = userData?.id || null;
          const forcedWorkerName =
            `${userData?.user?.first_name || ""} ${userData?.user?.last_name || ""}`.trim() ||
            userData?.user?.email ||
            "My appointments";
       
          setCurrentWorkerId(forcedWorkerId);
          currentWorkerIdRef.current = forcedWorkerId;
          setCurrentWorkerName(forcedWorkerName);

          setFormData((prev) => ({
            ...prev,
            worker_id: forcedWorkerId,
            master: forcedWorkerName,
          }));

          if (!forcedWorkerId) {
            console.error("Worker resolve failed. userData:", userData);
            toast.error("Worker id was not returned by backend.");
          }
        } else {
          setCurrentWorkerId(null);
          currentWorkerIdRef.current = null;
          setCurrentWorkerName("");
        }

        const servicesRes = await fetch(`${API_URL}/service/my-services/`, {
          headers,
        });
        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices(Array.isArray(servicesData) ? servicesData : []);
        } else {
          setServices([]);
        }

        const realEstateRes = await fetch(
          `${API_URL}/service/real-estate-my-listings/`,
          { headers },
        );
        if (realEstateRes.ok) {
          const realEstateData = await realEstateRes.json();
          setRealEstates(
            Array.isArray(realEstateData)
              ? realEstateData.map((estate) => ({
                  id: estate.id,
                  property_name: estate.property_name || "Unnamed property",
                  address: estate.address || "No address",
                  listing_type: estate.listing_type || "Unknown",
                  property_type: estate.property_type,
                  sell_price: estate.sell_price,
                  monthly_rent_price: estate.monthly_rent_price,
                  ...estate,
                }))
              : [],
          );
        } else {
          setRealEstates([]);
        }
      } catch (err) {
        console.error("Error fetching appointment modal data:", err);
        toast.error(err?.message || "Failed to load appointment form");
      } finally {
        setInitialDataLoading(false);
      }
    };

    fetchAllData();
  }, [isOpen]);

  const resetForm = () => {
    const now = new Date();

    setFormData({
      client: "",
      client_id: null,
      master: isWorker ? currentWorkerName : "",
      worker_id: isWorker ? effectiveWorkerId : null,
      service_type: "",
      service_id: null,
      service_name: "",
      is_other_service: false,
      price: "",
      date: `${now.toLocaleString("en-US", {
        month: "long",
      })} ${now.getDate()}`,
      time: "08:00",
      duration: { hours: 1, minutes: 0 },
      real_estate_id: null,
    });

    setOpenDropdown(null);
  };

  const getCurrentServiceListingValue = () => {
    if (formData.service_type === "service" && formData.service_id) {
      return `service_${formData.service_id}`;
    }
    if (formData.service_type === "listing" && formData.real_estate_id) {
      return `listing_${formData.real_estate_id}`;
    }
    if (formData.service_type === "other") {
      return "other";
    }
    return "";
  };

  const getServiceListingOptions = () => {
    const options = [
      {
        value: "",
        label: "Select service or listing",
        type: "placeholder",
        className: "custom-select-placeholder",
      },
    ];

    const activeServices = services.filter(
      (service) => service.on_off === true,
    );

    if (activeServices.length > 0) {
      options.push({
        value: "services_header",
        label: "Your Services",
        type: "header",
        isDisabled: true,
        align: "center",
        className: "custom-select-header static-header",
      });

      activeServices.forEach((service) => {
        options.push({
          value: `service_${service.id}`,
          label: service.name || service.title || "Unnamed service",
          type: "service",
          originalData: service,
          className: "custom-select-service",
        });
      });
    }

    if (realEstates.length > 0) {
      options.push({
        value: "listings_header",
        label: "Your Listings",
        type: "header",
        isDisabled: true,
        align: "center",
        className: "custom-select-header static-header",
      });

      realEstates.forEach((estate) => {
        options.push({
          value: `listing_${estate.id}`,
          label: estate.property_name,
          type: "listing",
          originalData: estate,
          className: "custom-select-listing",
        });
      });
    }

    options.push({
      value: "other",
      label: "Other (custom service)",
      type: "other",
      className: "custom-select-other",
    });

    return options;
  };

  const handleServiceListingChange = (option) => {
    const { value, type, originalData } = option;

    triggerStartAppointment();

    if (value === "") {
      setFormData((prev) => ({
        ...prev,
        service_type: "",
        service_id: null,
        service_name: "",
        is_other_service: false,
        price: "",
        duration: { hours: 1, minutes: 0 },
        real_estate_id: null,
      }));
      return;
    }

    switch (type) {
      case "service": {
        const service = originalData;
        const durationHours = Math.floor((service.duration || 0) / 60);
        const durationMins = (service.duration || 0) % 60;

        let priceStr = "";
        if (
          service.price !== undefined &&
          service.price !== null &&
          service.price !== ""
        ) {
          const priceNum = Number(service.price);
          priceStr = !Number.isNaN(priceNum)
            ? `${currencySymbol}${priceNum.toFixed(2)}`
            : `${currencySymbol}0.00`;
        }

        // Reset worker if not assigned to this service (like mobile workerForAppointment filter)
        const serviceWorkers = Array.isArray(service.workers) ? service.workers : [];
        const workerResetFields =
          serviceWorkers.length > 0 && formData.worker_id && !serviceWorkers.includes(formData.worker_id)
            ? { worker_id: null, master: "" }
            : {};

        setFormData((prev) => ({
          ...prev,
          service_type: "service",
          service_id: service.id,
          service_name: service.name || service.title || "Unnamed service",
          is_other_service: false,
          price: priceStr,
          duration: { hours: durationHours, minutes: durationMins },
          real_estate_id: null,
          ...workerResetFields,
        }));
        break;
      }

      case "listing": {
        const estate = originalData;
        let price = "";

        if (estate.listing_type?.toLowerCase() === "rent") {
          price = estate.monthly_rent_price || "";
        } else if (estate.listing_type?.toLowerCase() === "sale") {
          price = estate.sell_price || "";
        } else {
          price = estate.sell_price || estate.monthly_rent_price || "";
        }

        let priceStr = "";
        if (price !== undefined && price !== null && price !== "") {
          const priceNum = Number(price);
          priceStr = !Number.isNaN(priceNum)
            ? `${currencySymbol}${priceNum.toFixed(2)}`
            : `${currencySymbol}0.00`;
        }

        setFormData((prev) => ({
          ...prev,
          service_type: "listing",
          service_id: null,
          service_name: estate.property_name,
          is_other_service: false,
          price: priceStr,
          duration: { hours: 1, minutes: 0 },
          real_estate_id: estate.id,
        }));
        break;
      }

      case "other":
        setFormData((prev) => ({
          ...prev,
          service_type: "other",
          service_id: null,
          service_name: "",
          is_other_service: true,
          price: "",
          duration: { hours: 1, minutes: 0 },
          real_estate_id: null,
        }));
        break;

      default:
        break;
    }
  };

  const handlePriceChange = (e) => {
    const normalized = normalizePriceInput(e.target.value, currencySymbol);

    setFormData((prev) => ({
      ...prev,
      price: normalized,
    }));

    triggerStartAppointment();
  };

  const sendAppointment = async (confirmed) => {
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) throw new Error("No access token");
    if (!startRef.current || !endRef.current) {
      throw new Error("Invalid appointment times");
    }

    const totalDuration =
      (formData.duration?.hours || 0) * 60 + (formData.duration?.minutes || 0);

    if (totalDuration <= 0) {
      throw new Error("Duration must be greater than 0");
    }

    const tzOffset = startRef.current.getTimezoneOffset() * 60000;
    const priceValue = parsePriceToNumber(formData.price, currencySymbol);

    const body = {
      price: priceValue,
      currency: userCurrency || "USD",
      appointment_date_time: new Date(
        startRef.current.getTime() - tzOffset,
      ).toISOString(),
      appointment_end_time: new Date(
        endRef.current.getTime() - tzOffset,
      ).toISOString(),
      duration: totalDuration,
      time_unit: "minutes",
      confirmed,
    };

    if (formData.service_type === "service" && formData.service_id) {
      body.service = formData.service_id;
    } else if (formData.service_type === "listing" && formData.real_estate_id) {
      body.listing = formData.real_estate_id;
    } else if (formData.service_type === "other") {
      if (!formData.service_name || !formData.service_name.trim()) {
        throw new Error("Service name is required for custom service");
      }
      body.is_other_service = true;
      body.new_service_name = formData.service_name.trim();
    } else {
      throw new Error("Service or listing must be selected");
    }

    if (formData.client_id) {
      body.client = formData.client_id;
    } else if (formData.client && formData.client.trim()) {
      body.new_client = true;
      body.client_name = formData.client.trim();
    } else {
      throw new Error("Client information is required");
    }

    if (isWorker) {
      if (!effectiveWorkerId) {
        throw new Error("Current worker could not be identified");
      }

      body.workers = [effectiveWorkerId];
    } else if (!isIndividual) {
      if (!formData.worker_id) {
        throw new Error("Master must be selected");
      }

      body.workers = [formData.worker_id];
    }

    const headers = buildHeaders(token);

    const res = await fetch(`${API_URL}/client/appointments/`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    const text = await res.text();
    let data = null;

    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw {
        code: 500,
        message: "Server returned invalid response",
        rawResponse: text,
      };
    }

    if (!res.ok) {
      throw data;
    }

    return data;
  };

  const handleConfirmOverlap = async () => {
    try {
      setConfirmModalOpen(false);
      setLoading(true);
      await sendAppointment(true);
      toast.success("Appointment added successfully!");
      resetForm();
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Confirm overlap error:", err);
      toast.error(err?.message || err?.detail || "Error creating appointment");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    console.log("handleSubmit fired", {
      isWorker,
      currentWorkerId,
      formWorkerId: formData.worker_id,
      effectiveWorkerId,
      formData,
    });

    if (initialDataLoading) {
      toast.error("Please wait until worker data is loaded");
      return;
    }

    const errors = [];

    if (!formData.client.trim() && !formData.client_id) {
      errors.push("Enter client name or select existing client");
    }

    const parsedPrice = parsePriceToNumber(formData.price, currencySymbol);

    if (!formData.price.trim()) {
      errors.push("Enter a price");
    } else if (Number.isNaN(parsedPrice) || parsedPrice <= 0) {
      errors.push("Price must be a valid number");
    }

    if (!formData.date) errors.push("Choose a date");
    if (!formData.time) errors.push("Select start time");

    const totalDuration =
      (formData.duration?.hours || 0) * 60 + (formData.duration?.minutes || 0);

    if (totalDuration <= 0) {
      errors.push("Set duration greater than 0");
    }

    if (shouldShowMasterSelect && !formData.worker_id) {
      errors.push("Select a master");
    }

    if (isWorker && !effectiveWorkerId) {
      errors.push("Current worker could not be identified");
    }

    if (!formData.service_type) {
      errors.push("Select either a service or a listing");
    }

    if (formData.service_type === "other" && !formData.service_name.trim()) {
      errors.push("Enter service name for 'Other'");
    }

    if (errors.length > 0) {
      console.error("Appointment validation errors:", errors);
      toast.error(errors[0], { duration: 5000 });
      return;
    }

    const dateObj = parseDateString(formData.date || defaultDate);
    const [hours, minutes] = (formData.time || "08:00").split(":").map(Number);

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      toast.error("Invalid time selected");
      return;
    }

    dateObj.setHours(hours, minutes, 0, 0);

    const start = new Date(dateObj);
    if (Number.isNaN(start.getTime())) {
      toast.error("Invalid date or time selected");
      return;
    }

    const durationH = formData.duration?.hours || 0;
    const durationM = formData.duration?.minutes || 0;
    const end = new Date(
      start.getTime() + (durationH * 60 + durationM) * 60000,
    );

    startRef.current = start;
    endRef.current = end;

    setLoading(true);
    try {
      await sendAppointment(false);
      toast.success("Appointment added successfully!");
      resetForm();
      onSuccess?.();
      onClose?.();
    } catch (err) {
      // console.error("Create appointment error:", err);

      if (err?.code === 412 || err?.code === 414) {
        setConfirmMessage(err.message);
        setConfirmModalOpen(true);
      } else if (err?.price?.[0]) {
        toast.error(err.price[0]);
      } else if (err?.workers?.[0]) {
        toast.error(err.workers[0]);
      } else {
        toast.error(
          err?.message || err?.detail || "Error creating appointment",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="appointment-page modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="appointment-page modal"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
          >
            <div className="appointment-page modal-header">
              <h2>Add appointment</h2>
              <button
                type="button"
                className="appointment-page close-btn"
                onClick={onClose}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="appointment-page form">
              <div className="appointment-page form-row">
                <div className="appointment-page form-group">
                  <label>Client name</label>
                  <ClientDropdown
                    value={formData.client}
                    onChange={(name, id) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({
                        ...prev,
                        client: name,
                        client_id: id,
                      }));
                    }}
                  />
                </div>

                {shouldShowMasterSelect && (
                  <div className="appointment-page form-group master-field">
                    <CustomSelect
                      label="Master"
                      placeholder="Select master"
                      value={formData.worker_id || ""}
                      onChange={(option) => {
                        triggerStartAppointment();
                        setFormData((prev) => ({
                          ...prev,
                          worker_id: option.value,
                          master: option.label || "",
                        }));
                      }}
                      options={filteredWorkers.map((w) => ({
                        value: w.id,
                        label: w.registered
                          ? w.name
                          : `${w.name} (not registered)`,
                        disabled: !w.registered,
                      }))}
                    />
                  </div>
                )}
              </div>

              <div className="appointment-page form-group">
                <CustomSelect
                  label="Type of Service / Listing"
                  placeholder="Select service or listing"
                  value={getCurrentServiceListingValue()}
                  onChange={handleServiceListingChange}
                  options={getServiceListingOptions()}
                  isOptionDisabled={(option) => option.isDisabled === true}
                />
              </div>

              {formData.service_type === "other" && (
                <div className="appointment-page form-group">
                  <label>Service name</label>
                  <input
                    type="text"
                    placeholder="Enter service name"
                    value={formData.service_name}
                    className="input-cust"
                    onChange={(e) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({
                        ...prev,
                        service_name: e.target.value,
                      }));
                    }}
                  />
                </div>
              )}

              <div className="appointment-page form-row">
                <div className="appointment-page form-group">
                  <label>Price</label>
                  <input
                    type="text"
                    placeholder={`${currencySymbol}30.00`}
                    value={formData.price}
                    className="input-cust"
                    onChange={handlePriceChange}
                  />
                  <small className="text-gray-500 text-xs mt-1 block">
                    {formData.service_type === "service"
                      ? `Service price automatically filled in ${userCurrency} (${currencySymbol})`
                      : formData.service_type === "listing"
                        ? `Listing price automatically filled in ${userCurrency} (${currencySymbol})`
                        : formData.service_type === "other"
                          ? `Enter custom price for your service in ${userCurrency} (${currencySymbol})`
                          : `Price will be filled when you select a service or listing in ${userCurrency} (${currencySymbol})`}
                  </small>
                </div>
              </div>

              <div className="appointment-page form-row">
                <div className="appointment-page form-group">
                  <label>Date</label>
                  <CalendarDropdown
                    id="calendar"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    value={formData.date}
                    onChange={(value) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({ ...prev, date: value }));
                    }}
                  />
                </div>

                <div className="appointment-page form-group">
                  <label>Start time</label>
                  <CustomTimePicker
                    id="time"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    value={formData.time}
                    onChange={(value) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({ ...prev, time: value }));
                    }}
                  />
                </div>

                <div className="appointment-page form-group">
                  <label>Duration</label>
                  <DurationPicker
                    id="duration"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    value={formData.duration}
                    onChange={(value) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({ ...prev, duration: value }));
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="appointment-page modal-footer">
              <button
                type="button"
                className="appointment-page cancel"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="appointment-page create"
                onClick={() => {
                  handleSubmit();
                }}
                disabled={loading || initialDataLoading}
              >
                {initialDataLoading
                  ? "Loading..."
                  : loading
                    ? "Saving..."
                    : "Create"}
              </button>
            </div>
          </motion.div>

          <ConfirmModal
            isOpen={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={handleConfirmOverlap}
            message={confirmMessage}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
